import { useState, useCallback } from 'react'
import { env } from '@/config/env.ts'
import { getFriendlyTransactionError } from '@/utils/walletError.ts'
import { useContract } from './useContract.ts'
import { useWallet } from './useWallet.ts'
import type {
  Vault,
  CreateVaultParams,
  ContractWriteResult,
} from '@/services/contract/contract.types.ts'

interface UseVaultsState {
  vaultIds: bigint[]
  vaultDetails: Map<string, Vault>
  totalVaults: bigint | null
  loading: boolean
  error: string | null
}

interface UseVaultsReturn extends UseVaultsState {
  fetchMyVaults: () => Promise<bigint[]>
  fetchVaultDetails: (vaultId: bigint) => Promise<Vault | null>
  fetchTotalVaults: () => Promise<void>
  getTotalVaultsCount: () => Promise<bigint>
  getTimeRemaining: (vaultId: bigint) => Promise<bigint | null>
  createVault: (
    params: CreateVaultParams,
  ) => Promise<ContractWriteResult | null>
  resetCountdown: (vaultId: bigint) => Promise<ContractWriteResult | null>
  startCountdown: (vaultId: bigint) => Promise<ContractWriteResult | null>
  updateBeneficiary: (
    vaultId: bigint,
    newBeneficiary: `0x${string}`,
  ) => Promise<ContractWriteResult | null>
}

export function useVaults(): UseVaultsReturn {
  const contract = useContract()
  const { address } = useWallet()

  const [state, setState] = useState<UseVaultsState>({
    vaultIds: [],
    vaultDetails: new Map(),
    totalVaults: null,
    loading: false,
    error: null,
  })

  const setLoading = () =>
    setState((s) => ({ ...s, loading: true, error: null }))
  const setError = (error: string) =>
    setState((s) => ({ ...s, loading: false, error }))

  const fetchMyVaults = useCallback(async (): Promise<bigint[]> => {
    setLoading()
    try {
      const vaultIds = await contract.getMyVaults(
        address ? (address as `0x${string}`) : undefined,
      )
      setState((s) => ({ ...s, vaultIds, loading: false }))
      return vaultIds
    } catch (e) {
      setError(getFriendlyTransactionError(e))
      setState((s) => ({ ...s, loading: false }))
      return []
    }
  }, [contract, address])

  const fetchVaultDetails = useCallback(
    async (vaultId: bigint): Promise<Vault | null> => {
      setLoading()
      try {
        const vault = await contract.getVaultDetails(
          vaultId,
          address ? (address as `0x${string}`) : undefined,
        )
        if (env.enableDebug) {
          console.log('[useVaults] getVaultDetails decoded:', {
            vaultId: vaultId.toString(),
            vault,
          })
        }
        setState((s) => {
          const updated = new Map(s.vaultDetails)
          updated.set(vaultId.toString(), vault)
          return { ...s, vaultDetails: updated, loading: false }
        })
        return vault
      } catch (e) {
        if (env.enableDebug) {
          console.error('[useVaults] getVaultDetails error:', e)
        }
        setError(getFriendlyTransactionError(e))
        return null
      }
    },
    [contract, address],
  )

  const fetchTotalVaults = useCallback(async () => {
    setLoading()
    try {
      const totalVaults = await contract.getTotalVaults()
      setState((s) => ({ ...s, totalVaults, loading: false }))
    } catch (e) {
      setError(getFriendlyTransactionError(e))
    }
  }, [contract])

  const getTotalVaultsCount = useCallback(async (): Promise<bigint> => {
    return contract.getTotalVaults()
  }, [contract])

  const getTimeRemaining = useCallback(
    async (vaultId: bigint): Promise<bigint | null> => {
      try {
        return await contract.getTimeRemaining(vaultId)
      } catch (e) {
        setError(getFriendlyTransactionError(e))
        return null
      }
    },
    [contract],
  )

  const createVault = useCallback(
    async (params: CreateVaultParams): Promise<ContractWriteResult | null> => {
      setLoading()
      try {
        const result = await contract.createVault(params)
        setState((s) => ({ ...s, loading: false }))
        return result
      } catch (e) {
        setError(getFriendlyTransactionError(e))
        setState((s) => ({ ...s, loading: false }))
        throw e
      }
    },
    [contract],
  )

  const resetCountdown = useCallback(
    async (vaultId: bigint): Promise<ContractWriteResult | null> => {
      setLoading()
      try {
        const result = await contract.resetCountdown(vaultId)
        setState((s) => ({ ...s, loading: false }))
        return result
      } catch (e) {
        setError(getFriendlyTransactionError(e))
        return null
      }
    },
    [contract],
  )

  const startCountdown = useCallback(
    async (vaultId: bigint): Promise<ContractWriteResult | null> => {
      setLoading()
      try {
        const result = await contract.startCountdown(vaultId)
        setState((s) => ({ ...s, loading: false }))
        return result
      } catch (e) {
        setError(getFriendlyTransactionError(e))
        return null
      }
    },
    [contract],
  )

  const updateBeneficiary = useCallback(
    async (
      vaultId: bigint,
      newBeneficiary: `0x${string}`,
    ): Promise<ContractWriteResult | null> => {
      setLoading()
      try {
        const result = await contract.updateBeneficiary(vaultId, newBeneficiary)
        setState((s) => ({ ...s, loading: false }))
        return result
      } catch (e) {
        setError(getFriendlyTransactionError(e))
        return null
      }
    },
    [contract],
  )

  return {
    ...state,
    fetchMyVaults,
    fetchVaultDetails,
    fetchTotalVaults,
    getTotalVaultsCount,
    getTimeRemaining,
    createVault,
    resetCountdown,
    startCountdown,
    updateBeneficiary,
  }
}
